/**
 * Form controls — roadmap 4.2.
 *
 * Each one picks up its `id` and `aria-*` from the surrounding `<Field>`, so a
 * caller writes
 *
 *     <Field label={t('course.title')} error={errors.title_en?.message} required>
 *       <Input {...register('title_en')} />
 *     </Field>
 *
 * and gets the label association, the invalid state and the described-by wiring
 * without repeating them.
 *
 * `Select` is not here: it needs a portal and a styled option list, so it lives
 * in `Select.tsx` on Radix (§4.2). Everything in this file is a native element
 * under a custom appearance, which is what keeps keyboard behaviour, form
 * participation and the indeterminate checkbox state for free.
 */
import { Check, Upload } from 'lucide-react'

import { cn } from './cn'
import { useFieldProps } from './fieldContext'

const CONTROL = [
  'w-full rounded-[5px] border border-line-strong bg-surface text-fg',
  'placeholder:text-fg-subtle',
  'transition-colors',
  'aria-invalid:border-danger aria-invalid:ring-1 aria-invalid:ring-danger/30',
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:opacity-70',
]

// ---------------------------------------------------------------------------
/**
 * ⚠️ **`WithRef`, not `WithoutRef`.** React 19 passes `ref` to a function
 * component as an ordinary prop, and `register()` from `react-hook-form` has
 * always relied on that here — it was reaching the `<input>` at runtime while
 * the type said the prop did not exist, which the spread through `register()`
 * hid. A caller holding its own `useRef` (the door scanner, which refocuses
 * the code box after every scan) is the case that made the gap visible.
 */
export type InputProps = React.ComponentPropsWithRef<'input'>

export function Input({ className, ...props }: InputProps) {
  const field = useFieldProps()
  return <input {...field} className={cn(CONTROL, 'h-10 px-3 text-sm', className)} {...props} />
}

// ---------------------------------------------------------------------------
export type TextareaProps = React.ComponentPropsWithoutRef<'textarea'>

export function Textarea({ className, rows = 4, ...props }: TextareaProps) {
  const field = useFieldProps()
  return (
    <textarea
      {...field}
      rows={rows}
      className={cn(CONTROL, 'resize-y px-3 py-2 text-sm', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
export type CheckboxProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'type'> & {
  label: string
  description?: string | undefined
}

/**
 * Checkbox with its own label, since a checkbox's label belongs beside it rather
 * than above like every other control.
 *
 * A native input under a custom appearance: the platform gives keyboard support,
 * the indeterminate state and form participation for free, and the tick is drawn
 * with `peer-checked`.
 */
export function Checkbox({ label, description, className, ...props }: CheckboxProps) {
  const field = useFieldProps()

  return (
    <label className={cn('flex cursor-pointer items-start gap-2.5 text-sm', className)}>
      <span className="relative mt-0.5 grid size-4.5 shrink-0 place-items-center">
        <input
          type="checkbox"
          {...field}
          className={cn(
            'peer size-4.5 appearance-none rounded border border-line-strong bg-surface',
            // The edge is `brand-fg` so the checked box's outline clears 3:1
            // on the dark surface (600 there is 2.47:1); the fill stays 600
            // because it carries the white tick.
            'checked:border-brand-fg checked:bg-brand-600',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
          {...props}
        />
        <Check
          aria-hidden="true"
          className="pointer-events-none absolute size-3.5 text-fg-onbrand opacity-0 peer-checked:opacity-100"
        />
      </span>
      <span className="min-w-0">
        <span className="block font-medium">{label}</span>
        {description ? <span className="block text-xs text-fg-muted">{description}</span> : null}
      </span>
    </label>
  )
}

// ---------------------------------------------------------------------------
export type RadioOption = { value: string; label: string; description?: string | undefined }

export type RadioGroupProps = {
  name: string
  options: readonly RadioOption[]
  value?: string | undefined
  onChange?: React.ChangeEventHandler<HTMLInputElement> | undefined
  className?: string | undefined
  /** Group label, for the fieldset that holds the options together. */
  legend: string
  legendHidden?: boolean | undefined
}

export function RadioGroup({
  name,
  options,
  value,
  onChange,
  className,
  legend,
  legendHidden = true,
}: RadioGroupProps) {
  return (
    <fieldset className={cn('space-y-2', className)}>
      {/* A radio group without a fieldset and legend is a pile of unrelated
          controls to a screen reader — it cannot say what the choice is about. */}
      <legend className={cn('text-sm font-medium', legendHidden && 'sr-only')}>{legend}</legend>
      {options.map((option) => (
        <label
          key={option.value}
          className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line p-3 text-sm hover:bg-sunken has-checked:border-brand-fg has-checked:bg-brand-50"
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === undefined ? undefined : value === option.value}
            onChange={onChange}
            // ⚠️ `accent-brand-fg`, not the 600 accent: the dot is the
            // selection, and the dark 600 measures ~2.5:1 on the dark ground.
            className="mt-0.5 size-4 shrink-0 accent-brand-fg"
          />
          <span className="min-w-0">
            <span className="block font-medium">{option.label}</span>
            {option.description ? (
              <span className="block text-xs text-fg-muted">{option.description}</span>
            ) : null}
          </span>
        </label>
      ))}
    </fieldset>
  )
}

// ---------------------------------------------------------------------------
export type FileInputProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'type'> & {
  /** Button text, e.g. "Choose an image". */
  buttonLabel: string
  /** Name of the chosen file, or a hint when nothing is chosen. */
  fileLabel?: string | undefined
}

/**
 * File input.
 *
 * The native control's own button text cannot be translated — the browser picks
 * it from the OS locale, so an Arabic interface shows an English "Choose File"
 * next to Arabic labels. Hiding it behind our own button is the only way to get
 * that string into the locale bundle.
 */
export function FileInput({ buttonLabel, fileLabel, className, ...props }: FileInputProps) {
  const field = useFieldProps()

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <label
        className={cn(
          'inline-flex h-10 cursor-pointer items-center gap-2 rounded-[5px] border border-line-strong',
          'bg-surface px-4 text-sm font-medium hover:bg-sunken',
          'focus-within:outline focus-within:outline-2 focus-within:outline-focus',
        )}
      >
        <Upload aria-hidden="true" className="size-4" />
        {buttonLabel}
        <input type="file" {...field} className="sr-only" {...props} />
      </label>
      {fileLabel ? <span className="truncate text-sm text-fg-muted">{fileLabel}</span> : null}
    </div>
  )
}
